import React, { useState, useEffect } from 'react';
import { AlertTriangle, Mail, CheckCircle, RefreshCw, Calendar, Image, Package } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useGetCurrentUserQuery, useGetUserStatisticsQuery, useResendVerificationEmailMutation } from '../../store/services/api';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import UserQuotaOverview from '../../components/dashboard/UserQuotaOverview';
import StatisticsOverview from '../../components/dashboard/StatisticsOverview';
import BuyMoreCredits from '../../components/modals/BuyMoreCredits';
import PaymentSuccess from '../../components/modals/PaymentSuccess';
import PaymentFailed from '../../components/modals/PaymentFailed';

const Overview = () => {
  const [isBuyCreditsOpen, setIsBuyCreditsOpen] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [showVerifiedMessage, setShowVerifiedMessage] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  
  // Redux state
  const { user } = useSelector((state: RootState) => state.auth);
  
  // RTK Query hooks
  const { data: currentUser } = useGetCurrentUserQuery(undefined);
  const { data: statistics } = useGetUserStatisticsQuery(undefined);
  const [resendVerificationEmail] = useResendVerificationEmailMutation();

  // Check verification status on component mount
  useEffect(() => {
    if (currentUser?.is_verified) {
      const verificationShown = localStorage.getItem('verification_success_shown');
      if (!verificationShown) {
        setShowVerifiedMessage(true);
        localStorage.setItem('verification_success_shown', 'true');
        
        const timer = setTimeout(() => {
          setShowVerifiedMessage(false);
        }, 10000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [currentUser]);

  // Resend verification email
  const handleResendVerification = async () => {
    if (!currentUser?.email) return;
    
    setResendingEmail(true);
    try {
      await resendVerificationEmail({ email: currentUser.email }).unwrap();
      toast.success('Verification email sent successfully!');
    } catch (error: any) {
      const errorMessage = error?.data?.message || 'Failed to send verification email. Please try again later.';
      toast.error(errorMessage);
    } finally {
      setResendingEmail(false);
    }
  };

  // URL ve localStorage'dan payment durumunu kontrol et
  useEffect(() => {
    // URL'den parametreleri al
    const params = new URLSearchParams(location.search);
    const successPayment = params.get('success_payment');
    const paymentCanceled = params.get('payment_canceled');
    const sessionIdFromUrl = params.get('session_id');

    // localStorage'dan pending session'ı al
    const pendingSessionId = localStorage.getItem('stripe_pending_session_id');

    // Session ID'yi belirle (önce URL'den, yoksa localStorage'dan)
    const activeSessionId = sessionIdFromUrl || pendingSessionId;

    if (activeSessionId) {
      setSessionId(activeSessionId);

      // Başarılı ödeme
      if (successPayment === 'true') {
        setIsSuccessModalOpen(true);
      }
      // Başarısız/iptal edilen ödeme
      else if (paymentCanceled === 'true') {
        setIsFailedModalOpen(true);
      }
    }

    // URL'i temizle
    if (location.search) {
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location]);

  return (
    <div className="space-y-6">
      {/* Email Verification Alert */}
      {currentUser && !currentUser.is_verified && (
        <div className="bg-amber-50 border border-amber-200 p-3 sm:p-4 rounded-lg">
          <div className="flex flex-col sm:flex-row sm:items-start">
            <AlertTriangle className="w-5 h-5 text-amber-500 mb-2 sm:mb-0 sm:mr-3 sm:mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-amber-800">Email Verification Required</h3>
              <p className="text-xs sm:text-sm text-amber-700 mt-1">
                Please verify your email address to unlock all features. We've sent a verification link to <strong className="break-all">{currentUser.email}</strong>.
              </p>
              <div className="mt-3">
                <button
                  onClick={handleResendVerification}
                  disabled={resendingEmail}
                  className="inline-flex items-center text-xs font-medium text-amber-800 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 rounded-md px-2.5 py-1.5 transition-colors"
                >
                  {resendingEmail ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-3.5 h-3.5 mr-1.5" />
                      Resend Verification Email
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Verified Success Message */}
      {currentUser && currentUser.is_verified && showVerifiedMessage && (
        <div className="bg-green-50 border border-green-200 p-3 sm:p-4 rounded-lg animate-fadeIn">
          <div className="flex flex-col sm:flex-row sm:items-start">
            <CheckCircle className="w-5 h-5 text-green-500 mb-2 sm:mb-0 sm:mr-3 sm:mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-green-800">Email Verified</h3>
              <p className="text-xs sm:text-sm text-green-700 mt-1">
                Your email address <strong className="break-all">{currentUser.email}</strong> has been verified. You have full access to all features.
              </p>
              <div className="mt-2 flex justify-end">
                <button 
                  onClick={() => setShowVerifiedMessage(false)}
                  className="text-xs text-green-700 hover:text-green-900"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* App Guidance */}
      <div className="bg-white p-3 sm:p-5 rounded-lg border border-gray-100">
        <h3 className="text-sm sm:text-base font-medium mb-3 sm:mb-4">Quick Start Guide</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="flex items-start">
            <div className="w-8 h-8 rounded-md bg-[#FFEEEE] flex items-center justify-center mr-3 flex-shrink-0">
              <Calendar className="w-4 h-4 text-[#FF4444]" />
            </div>
            <div>
              <h3 className="text-sm font-medium mb-1">1. Create Event</h3>
              <p className="text-xs text-gray-500 leading-relaxed">Start by creating a new event for your photo collection</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="w-8 h-8 rounded-md bg-[#FFEEEE] flex items-center justify-center mr-3 flex-shrink-0">
              <Image className="w-4 h-4 text-[#FF4444]" />
            </div>
            <div>
              <h3 className="text-sm font-medium mb-1">2. Upload Photos</h3>
              <p className="text-xs text-gray-500 leading-relaxed">Upload and organize photos for your event</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="w-8 h-8 rounded-md bg-[#FFEEEE] flex items-center justify-center mr-3 flex-shrink-0">
              <Package className="w-4 h-4 text-[#FF4444]" />
            </div>
            <div>
              <h3 className="text-sm font-medium mb-1">3. Share & Manage</h3>
              <p className="text-xs text-gray-500 leading-relaxed">Share your collection and manage your account</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-6">
        <UserQuotaOverview 
          showBuyCredits={true}
          onBuyCreditsClick={() => setIsBuyCreditsOpen(true)}
        />
        <StatisticsOverview />
      </div>

      {/* Modals */}
      <BuyMoreCredits 
        isOpen={isBuyCreditsOpen}
        onClose={() => setIsBuyCreditsOpen(false)}
      />

      <PaymentSuccess 
        isOpen={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
        sessionId={sessionId || undefined}
      />
      
      <PaymentFailed 
        isOpen={isFailedModalOpen}
        onClose={() => setIsFailedModalOpen(false)}
        sessionId={sessionId || undefined}
      />
    </div>
  );
};

export default Overview;